import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Button, Spin, Typography } from 'antd';
import connectAzureService from '../../services/azure/azureOnboarding/connectAzure.service';

const { Title, Paragraph, Text } = Typography;

const PAGE_TITLE = 'Connect Synapse 360 to Azure';

type PageState = 'loading' | 'ready' | 'notFound' | 'error';

// Public page (no login) that a client's Azure Global Administrator opens from the link an
// admin sends them. It never shows the token, the consent URL or any ids or names.
const ConnectAzure: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<PageState>('loading');
  const [connecting, setConnecting] = useState(false);

  const failureState = (err: any): PageState =>
    err?.response?.status === 404 ? 'notFound' : 'error';

  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    connectAzureService
      .resolveLink(token)
      .then(() => !cancelled && setState('ready'))
      .catch((err) => !cancelled && setState(failureState(err)));
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Resolve again on click: the consent URL is only valid for 24 hours after it's minted.
  const handleConnect = async () => {
    setConnecting(true);
    try {
      const consentUrl = await connectAzureService.resolveLink(token);
      window.location.assign(consentUrl);
    } catch (err) {
      setState(failureState(err));
      setConnecting(false);
    }
  };

  const renderBody = () => {
    switch (state) {
      case 'loading':
        return (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        );
      case 'notFound':
        return (
          <Alert
            type="error"
            showIcon
            message="This link isn't valid. Check you copied the whole link, or ask your MetrixData 360 contact for a new one."
          />
        );
      case 'error':
        return (
          <Alert
            type="error"
            showIcon
            message="We couldn't load this page. Please try again later."
          />
        );
      default:
        return (
          <>
            <Paragraph>
              MetrixData 360 uses the <strong>Synapse 360 Azure</strong> app to read your
              organisation&apos;s Azure cost, usage, reservation and savings plan data. Access is{' '}
              <strong>read-only</strong>.
            </Paragraph>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={
                <>
                  <strong>You must be a Global Administrator</strong> of your Microsoft Entra
                  tenant.
                </>
              }
            />
            <Paragraph>
              You&apos;ll sign in with Microsoft. First, approve the Synapse 360 Azure app for your
              organisation (admin consent). A second sign-in follows, often without a prompt, so
              that Synapse 360 can give its app read-only roles:
            </Paragraph>
            <ul>
              <li>Billing account reader (MCA) or EnrollmentReader (EA)</li>
              <li>Cost Management Reader on subscriptions</li>
              <li>Reservations Reader</li>
              <li>Savings plan Reader</li>
            </ul>
            <Paragraph>
              Some roles need elevated access (Entra ID → Properties → Access management for Azure
              resources). If anything can&apos;t be assigned automatically, the last page lists the
              manual steps.
            </Paragraph>
            <Paragraph>
              Buying Azure through a CSP partner? Your partner must also turn on cost visibility
              for your organisation.
            </Paragraph>
            <div style={{ textAlign: 'center', margin: '24px 0 16px' }}>
              <Button type="primary" size="large" loading={connecting} onClick={handleConnect}>
                Connect Synapse 360 to Azure
              </Button>
            </div>
            <Text type="secondary">If you weren&apos;t expecting this link, close this page.</Text>
          </>
        );
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#00274d',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 16px',
      }}
    >
      <img
        src={`${process.env.PUBLIC_URL}/assets/images/logo.png`}
        alt="MetrixData 360"
        style={{ height: 48, maxWidth: '80%', marginBottom: 24 }}
      />
      <div
        style={{
          background: '#fff',
          borderRadius: 8,
          padding: '32px 36px',
          width: '100%',
          maxWidth: 640,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
        }}
      >
        <Title level={3} style={{ marginTop: 0 }}>
          {PAGE_TITLE}
        </Title>
        {renderBody()}
      </div>
    </div>
  );
};

export default ConnectAzure;
